import { ErrorCodes } from '../../shared/api';

interface ScanErrorProps {
  // Server error code (see ErrorCodes in shared/api.ts) or null for unknown.
  code: string | null;
  // Raw server message as a fallback and/or display aid.
  message: string;
  // The photo(s) the user just tried — first one is shown as a dimmed thumbnail.
  thumbnails: string[];
  onRetake: () => void;
}

interface CopySet {
  title: string;
  body?: string;
  tips: string[];
  // "Try again" when the problem is transient (timeout/failed); "Retake"
  // when the user needs a better photo.
  primaryLabel: string;
}

function copyFor(code: string | null, message: string): CopySet {
  switch (code) {
    case ErrorCodes.OFFLINE:
      return {
        title: "You're offline",
        body: 'Scanning needs an internet connection. Reconnect and try again — saved menus still work offline.',
        tips: [
          'Check Wi-Fi or mobile signal',
          'Step outside if you\'re in a basement or dead zone',
        ],
        primaryLabel: 'Try again',
      };
    case ErrorCodes.NOT_A_MENU:
      return {
        title: "This doesn't look like a menu",
        body: "We couldn't find menu items in the photo.",
        tips: [
          'Fill the frame with the menu',
          'Keep it flat — avoid glare and shadows',
          'One page at a time works best',
        ],
        primaryLabel: 'Retake',
      };
    case ErrorCodes.GEMINI_TIMEOUT:
      return {
        title: 'Taking longer than expected',
        body: 'We gave up waiting. The connection is slow, or the menu is unusually dense.',
        tips: [
          'Check your signal or Wi-Fi',
          'Try again with a sharper photo',
        ],
        primaryLabel: 'Try again',
      };
    case ErrorCodes.GEMINI_FAILED:
      return {
        title: 'Something went wrong on our end',
        body: 'The parser errored before finishing. Not your fault.',
        tips: ['Try again in a moment'],
        primaryLabel: 'Try again',
      };
    case ErrorCodes.UNSUPPORTED_IMAGE:
      return {
        title: "We can't read this file type",
        body: 'We need a JPEG, PNG, WebP, or HEIC.',
        tips: [
          'Take a photo instead of a screenshot',
          'Avoid PDFs — photograph the paper menu',
        ],
        primaryLabel: 'Retake',
      };
    case ErrorCodes.IMAGE_TOO_LARGE:
      return {
        title: 'The photo is too big',
        body: "We tried to compress it but it's still oversized. Usually a hi-res video screenshot.",
        tips: ['Use your camera app to take a fresh photo'],
        primaryLabel: 'Retake',
      };
    case ErrorCodes.TOO_MANY_PAGES:
      return {
        title: 'Too many pages',
        body: 'We can parse up to 6 menu pages at once.',
        tips: ['Scan the most important pages first — save the rest for a second scan'],
        primaryLabel: 'Retake',
      };
    case ErrorCodes.RATE_LIMITED:
      return {
        title: 'Slow down a moment',
        body: "You've scanned a lot in a short window. Try again in a bit.",
        tips: ['Give it a minute, then retry'],
        primaryLabel: 'Try again',
      };
    case ErrorCodes.SPEND_CAP:
      return {
        title: 'We are at capacity',
        body: 'The translator is temporarily unavailable. Try again in a few hours.',
        tips: [],
        primaryLabel: 'Try again',
      };
    default:
      return {
        title: 'Something went wrong',
        body: message,
        tips: ['Retake the photo and try again'],
        primaryLabel: 'Retake',
      };
  }
}

export function ScanError({ code, message, thumbnails, onRetake }: ScanErrorProps) {
  const copy = copyFor(code, message);
  const first = thumbnails[0];

  return (
    <div className="min-h-dvh safe-top safe-bottom container-app flex flex-col" style={{ paddingBottom: 32 }}>
      <header className="flex items-center pt-4 pb-6">
        <button
          onClick={onRetake}
          aria-label="Close"
          style={{
            minWidth: 44,
            minHeight: 44,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            color: 'var(--color-primary)',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
      </header>

      {first && (
        <div
          style={{
            position: 'relative',
            aspectRatio: '4 / 5',
            background: 'var(--color-surface)',
            borderRadius: 16,
            overflow: 'hidden',
            marginBottom: 24,
          }}
        >
          <img
            src={first}
            alt="Your photo"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              opacity: 0.45,
              filter: 'blur(1px)',
            }}
          />
          {thumbnails.length > 1 && (
            <div
              style={{
                position: 'absolute',
                bottom: 10,
                right: 10,
                padding: '4px 10px',
                borderRadius: 9999,
                background: 'rgba(0,0,0,0.55)',
                color: '#FFF',
                fontSize: 11,
                fontWeight: 500,
              }}
            >
              +{thumbnails.length - 1} more
            </div>
          )}
        </div>
      )}

      <h1 style={{ fontSize: 24, fontWeight: 600, margin: '0 0 8px', letterSpacing: '-0.01em' }}>
        {copy.title}
      </h1>
      {copy.body && (
        <p style={{ fontSize: 15, color: 'var(--color-text-secondary)', lineHeight: 1.55, margin: '0 0 16px' }}>
          {copy.body}
        </p>
      )}
      {copy.tips.length > 0 && (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
          {copy.tips.map((tip, i) => (
            <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: 'var(--color-primary)' }}>
              <span aria-hidden style={{ color: 'var(--color-accent)', paddingTop: 2 }}>•</span>
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      )}

      <div style={{ flex: 1 }} />

      <div className="flex flex-col gap-2 pt-6">
        <button onClick={onRetake} className="btn-primary">
          {copy.primaryLabel}
        </button>
      </div>
    </div>
  );
}
